import {
  ensureVercelBlobPreload,
  isVercelBlobGateWarmComplete,
  subscribeVercelBlobPreloadProgress,
} from './vercelAssetPreloadGate'

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null
let listenerAttached = false

function isLikelyMobileTouchDevice(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false
  if (navigator.maxTouchPoints > 0) return true
  const ua = navigator.userAgent || ''
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua)
}

/**
 * When the app runs as an installed PWA, prefer landscape (matches manifest `orientation`).
 * Manifest handles launch orientation; Screen Orientation API covers in-session lock where supported
 * (often requires a user gesture on Android Chrome — we retry once on first pointerdown).
 */
export function initStandaloneLandscapeOrientation(): void {
  if (typeof window === 'undefined') return
  if (!isStandaloneDisplay() || !isSecureContext()) return
  if (!isLikelyMobileTouchDevice()) return

  const tryLockLandscape = (): void => {
    // `lock` exists at runtime where supported; TS `ScreenOrientation` typings omit it in some lib versions.
    const so = screen.orientation as
      | { lock?: (orientation: string) => Promise<void> }
      | undefined
      | null
    if (!so || typeof so.lock !== 'function') return
    void so.lock('landscape').catch(() => {})
  }

  tryLockLandscape()
  window.addEventListener(
    'pointerdown',
    () => {
      tryLockLandscape()
    },
    { once: true, passive: true },
  )
}

/** Call once at app bootstrap so `beforeinstallprompt` is captured app-wide. */
export function initPwaInstallPromptCapture(): void {
  if (typeof window === 'undefined' || listenerAttached) return
  listenerAttached = true
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault()
    deferredInstallPrompt = e as BeforeInstallPromptEvent
  })
}

export function peekDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredInstallPrompt
}

function takeDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  const d = deferredInstallPrompt
  deferredInstallPrompt = null
  return d
}

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/iPad|iPhone|iPod/i.test(ua)) return true
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
}

export function isSecureContext(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext === true
}

export type PwaInstallRunResult =
  | 'standalone'
  | 'insecure'
  | 'ios-instructions'
  | 'completed'

export const PWA_INSTALLED_ACK_KEY = 'eclipsera.pwa.installedAck'

function setInstalledAckFromUserChoice(): void {
  try {
    localStorage.setItem(PWA_INSTALLED_ACK_KEY, '1')
  } catch {
    /* quota / private mode */
  }
}

export async function hasInstalledRelatedWebApp(): Promise<boolean> {
  if (typeof navigator === 'undefined') return false
  const nav = navigator as Navigator & {
    getInstalledRelatedApps?: () => Promise<Array<{ id?: string; platform?: string }>>
  }
  if (typeof nav.getInstalledRelatedApps !== 'function') return false
  try {
    const apps = await nav.getInstalledRelatedApps()
    return Array.isArray(apps) && apps.length > 0
  } catch {
    return false
  }
}

/**
 * Run synchronously from a click handler so Chromium shows the install dialog.
 * @returns true if `.prompt()` was started.
 */
export function showPwaInstallPromptIfAvailable(): boolean {
  if (isStandaloneDisplay() || !isSecureContext() || isIosDevice()) return false
  const deferred = takeDeferredInstallPrompt()
  if (!deferred) return false
  void deferred
    .prompt()
    .then(() => deferred.userChoice)
    .then((choice) => {
      if (choice?.outcome === 'accepted') setInstalledAckFromUserChoice()
    })
    .catch(() => {})
  return true
}

export async function runPwaInstallWarmupOnly(options?: {
  onProgress?: (loaded: number, total: number) => void
}): Promise<PwaInstallRunResult> {
  if (isStandaloneDisplay()) return 'standalone'
  if (!isSecureContext()) return 'insecure'
  if (isVercelBlobGateWarmComplete()) {
    if (isIosDevice()) return 'ios-instructions'
    return 'completed'
  }

  const unsub = subscribeVercelBlobPreloadProgress((loaded, total) => {
    options?.onProgress?.(loaded, total)
  })
  const minDelay = new Promise<void>((r) => {
    window.setTimeout(r, 600)
  })

  try {
    await Promise.all([ensureVercelBlobPreload(), minDelay])
  } finally {
    unsub()
  }

  if (isIosDevice()) return 'ios-instructions'
  return 'completed'
}

export async function runPwaInstallFromUserGesture(options?: {
  onProgress?: (loaded: number, total: number) => void
}): Promise<PwaInstallRunResult> {
  if (isStandaloneDisplay()) return 'standalone'
  if (!isSecureContext()) return 'insecure'

  showPwaInstallPromptIfAvailable()
  return runPwaInstallWarmupOnly(options)
}
